import { useEffect, useRef, useSyncExternalStore } from "react";

/**
 * Flashroom inside the Flashsuite phone app (flashsuite-phone docs/APP-MODE.md). The app marks every
 * page it loads with `FlashsuiteApp/<n>` in the user agent; index.html's head script reads that mark
 * before the first paint and sets `data-in-app` on <html>, the one place the rest of the app asks.
 */
export function inApp(): boolean {
  return typeof document !== "undefined" && document.documentElement.hasAttribute("data-in-app");
}

/** What the page tells the app, version 1. */
type ToApp =
  | { v: 1; kind: "tab"; tab: string; path: string }
  | { v: 1; kind: "place"; path: string; tab: string | null; root: boolean }
  | { v: 1; kind: "ready" };

/** What the app tells the page, on the `flashsuite-app` event. */
type FromApp = { kind: "tab-again"; tab: string } | { kind: "shown" } | { kind: "hidden" };

interface AppWindow {
  ReactNativeWebView?: { postMessage: (data: string) => void };
}

/** Tells the app, when the page runs inside it. */
export function postToApp(message: ToApp): void {
  const bridge = (window as unknown as AppWindow).ReactNativeWebView;
  // Why no target origin: this is React Native's bridge, which takes the message alone, not window.postMessage.
  // oxlint-disable-next-line unicorn/require-post-message-target-origin
  if (inApp() && bridge) bridge.postMessage(JSON.stringify(message));
}

let readyTold = false;

/**
 * Tells the app, once per page load, that the first real screen is drawn: the app shows the page then, in
 * place of the app's mark (flashsuite-phone docs/APP-MODE.md section 10).
 */
export function tellAppReady(): void {
  if (readyTold || !inApp()) return;
  readyTold = true;
  postToApp({ v: 1, kind: "ready" });
}

function readFromApp(e: Event): FromApp | null {
  const d = (e as CustomEvent<unknown>).detail as Record<string, unknown> | null;
  if (!d || typeof d !== "object") return null;
  if (d.kind === "shown") return { kind: "shown" };
  if (d.kind === "hidden") return { kind: "hidden" };
  if (d.kind === "tab-again" && typeof d.tab === "string") return { kind: "tab-again", tab: d.tab };
  return null;
}

/** Hears what the app says, only inside the app; returns the way to stop. */
export function onFromApp(listen: (message: FromApp) => void): () => void {
  const heard = (e: Event) => {
    const m = inApp() ? readFromApp(e) : null;
    if (m) listen(m);
  };
  window.addEventListener("flashsuite-app", heard);
  return () => window.removeEventListener("flashsuite-app", heard);
}

let shownTimes = 0;
const shownWatchers = new Set<() => void>();
if (typeof window !== "undefined") {
  onFromApp((m) => {
    if (m.kind !== "shown") return;
    shownTimes += 1;
    for (const watcher of shownWatchers) watcher();
  });
}

/**
 * How many times the app has shown this screen again. Why: every tab of the app is its own page, so a
 * screen learns what another tab changed only by reading again; a screen's loading effect lists this
 * number, and reads again, quietly, each time the app shows it.
 */
export function useShownAgain(): number {
  return useSyncExternalStore(
    (watch) => {
      shownWatchers.add(watch);
      return () => shownWatchers.delete(watch);
    },
    () => shownTimes,
    () => 0,
  );
}

/** Runs `read` each time the app shows the screen again after it opened, for a screen that reads quietly by hand. */
export function useOnShownAgain(read: () => void): void {
  const times = useShownAgain();
  const at = useRef(times);
  const latest = useRef(read);
  latest.current = read;
  useEffect(() => {
    if (times === at.current) return;
    at.current = times;
    latest.current();
  }, [times]);
}
